<template>
  <FullScreenOverlay :initializing="transitioning">
    <div class="fetching-container" v-if="transitioning">
      <p class="lead text-muted no-margin-bottom">
        <i class="fa fa-circle-o-notch fa-spin"/> Preparing...
      </p>
    </div>

    <PrintLayoutContainer v-if="!transitioning">
      <div
        class="container top-bar"
        slot="top"
      >
        <div class="print-quota" v-if="isTrial">
          <p class="text-muted">
            {{ remainingPrints }} of {{ printQuota }} prints remaining this month
          </p>
        </div>
        <div class="print-actions">
          <p><button
            class="btn btn-block btn-primary"
            @click="printAndClose"><i class="fa fa-print"/> Print</button></p>
          <p><button class="btn btn-block btn-default" @click="closePreview"><i class="fa fa-times"/> Cancel</button></p>
        </div>
      </div>
      <div slot="body">
        <PrintPage>
          <p class="no-margin-bottom subheader-text"><small>Printed by <b>{{ user.name }} ({{ user.email }})</b> from folder <b class="fs-block">{{ studentName }}</b> on {{ currentTime }}</small></p>
          <p class="text-muted subheader-text" v-if="isTrial"><small>Per our legal Terms of Use, the print feature is intended to facilitate communication and collaboration, and not to be used for illicit saving or distribution of proprietary content. <b>{{ remainingPrints }} of {{ printQuota }} prints remaining</b> for this month.</small></p>
          <h1>{{ title }}</h1>
          <template v-if="!keyStandard.noStandard">
            <h2>{{ keyStandard.standard_set }}</h2>
            <div class="standard-panel">
              <h3><b>Standard {{ keyStandard.standard_short_ref }}</b>&nbsp;&nbsp; <i>{{ keyStandard.standard_title }}</i></h3>
              <div class="standard-body">
                <blockquote v-if="keyStandard.core_purpose">
                  <strong>Core Purpose</strong>
                  <p>{{ keyStandard.core_purpose }}</p>
                </blockquote>
                <div v-html="keyStandard.standard_text"/>
              </div>
            </div>
          </template>
          <h2>Annual Target</h2>
          <div class="print-panel">
            <WikiText :text="annualText" class="annual-target-text"/>
            <WikiText v-if="goalExampleText" :text="goalExampleText" />
          </div>
          <template v-if="objectives && objectives.length > 0">
            <h3>Quarterly Objectives</h3>
            <div
              class="quarterly-objective-panel"
              v-for="(obj, index) in objectives"
              :key="index">
              <p class="quarterly-objective-title">{{ obj.level }} Quarter: {{ obj.title }}</p>
              <WikiText :text="obj.text" />
            </div>
          </template>
        </PrintPage>
      </div>
    </PrintLayoutContainer>
  </FullScreenOverlay>
</template>

<script>
import FullScreenOverlay from '@/components/FullScreenOverlay'
import PrintLayoutContainer from '@/components/PrintLayoutContainer'
import WikiText from '@/components/WikiText.vue'
import PrintPage from '@/components/PrintPage'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'PrintableSavedAnchorPage',
  components: {
    FullScreenOverlay,
    PrintLayoutContainer,
    WikiText,
    PrintPage
  },
  props: {
    keyStandard: {
      type: Object,
      default: () => {
        return {
          noStandard: true
        }
      }
    },
    title: {
      type: String,
      required: true
    },
    annualText: {
      type: String,
      required: true
    },
    goalExampleText: {
      type: String,
      default: ''
    },
    objectives: {
      type: Array,
      default() {
        return []
      }
    },
    studentName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      transitioning: true
    }
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('contentAccess', ['printQuota', 'printCount']),
    ...mapGetters('user', ['isTrial']),
    currentTime() {
      return new Date()
        .toISOString()
        .split('T')
        .join(' ')
    },
    remainingPrints() {
      var remaining = this.printQuota - this.printCount
      return remaining > -1 ? remaining : 0
    }
  },
  methods: {
    closePreview() {
      this.$emit('update:show', false)
    },
    printAndClose() {
      window.print()
      this.$emit('print')
      this.closePreview()
    }
  },
  created() {
    this.transitioning = true

    // set a minimum "wait time" so the user perceives the transition of the printable overlay animated
    // without this, the overlay could appear very quickly and user might think they navigated to a new page
    setTimeout(() => {
      this.transitioning = false
    }, 500)
  }
}
</script>

<style scoped lang="less">
.rounded-border {
  border: solid #d3d3d3 1px;
  border-radius: 2px;
}
h3 {
  font-size: 14px;
  font-weight: 400;
}
h2 {
  font-size: 18px;
  font-weight: 700;
}
.standard-panel {
  .print-panel;
  blockquote {
    padding: 2px 12px;
    padding-bottom: 4px;
    margin-bottom: 8px;
    font-size: 12px;
    blockquote {
      font-style: italic;
    }
  }
}
.print-panel {
  .rounded-border;
  padding: 15px;
}
.quarterly-objective-panel {
  .print-panel;
  margin-bottom: 10px;
  .quarterly-objective-title {
    font-weight: 700;
    margin-bottom: 0;
  }
}
.fetching-container {
  text-align: center;
  padding: 32px;
}
.personalized-for {
  font-weight: 400;
  margin-top: 10px;
  color: #666666;
  font-size: 18px;
}
.subheader-text {
  line-height: 13px;
}
.top-bar {
  display: flex;
  max-width: 100%;
  .print-quota {
    flex: 2 1 auto;
    padding-top: 6px;
    text-align: right;
  }
  .print-actions {
    padding-left: 12px;
    flex: 0 1 auto;
    margin-left: auto;
  }
}

@media print {
  blockquote {
    border: 0;
  }
}
</style>
